import { apiSlice } from "../../../app/apiSlice";

const apiSliceWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: ["Invoice", "Fees"],
});

const invoiceApiSlice = apiSliceWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getInvoice: builder.query({
      query: (parameter) => `/fees/invoice${parameter}`,
      providesTags: ["Invoice"],
    }),
    createInvoice: builder.mutation({
      query: (values) => ({
        url: "/fees/invoice",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Invoice"],
    }),
    deleteInvoice: builder.mutation({
      query: (id) => ({
        url: `/fees/invoice/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoice"],
    }),

    deleteInvoices: builder.mutation({
      query: (invoiceIds) => ({
        url: "/fees/invoice/bulk",
        method: "DELETE",
        body: { invoice_ids: invoiceIds },
      }),
      invalidatesTags: ["Invoice"],
    }),

    confirmInvoice: builder.mutation({
      query: ({ invoiceId, amount_paid }) => ({
        url: `/fees/invoice/${invoiceId}/confirm`,
        method: "PATCH",
        body: { amount_paid },
      }),
      invalidatesTags: ["Invoice", "Fees"],
    }),

    getSchoolInvoices: builder.query({
      query: ({ status, has_evidence, page = 1, per_page = 20, session_id, term_id, classroom_id } = {}) => {
        const params = new URLSearchParams({ page, per_page });
        if (status) params.append("status", status);
        if (has_evidence) params.append("has_evidence", "true");
        if (session_id) params.append("session_id", session_id);
        if (term_id) params.append("term_id", term_id);
        if (classroom_id) params.append("classroom_id", classroom_id);
        return `/fees/invoice/school/all?${params}`;
      },
      providesTags: ["Invoice"],
    }),

    getClassroomInvoiceReport: builder.query({
      query: (classroomId) => `/fees/invoice/classroom/${classroomId}`,
      providesTags: ["Invoice"],
    }),

    previewInvoice: builder.query({
      query: ({ classroom_id, student_ids = [], items = [], term_id, session_id }) => {
        const params = new URLSearchParams();
        if (classroom_id) params.append("classroom_id", classroom_id);
        if (term_id) params.append("term_id", term_id);
        if (session_id) params.append("session_id", session_id);
        student_ids.forEach((id) => params.append("student_ids", id));
        items.forEach((id) => params.append("items", id));
        return `/fees/invoice/preview?${params}`;
      },
    }),

    checkExistingInvoices: builder.query({
      query: ({ student_ids = [], term_id, session_id }) => {
        const params = new URLSearchParams();
        if (term_id) params.append("term_id", term_id);
        if (session_id) params.append("session_id", session_id);
        student_ids.forEach((id) => params.append("student_ids", id));
        return `/fees/invoice/existing-check?${params}`;
      },
    }),

    getClassroomFees: builder.query({
      query: (classroomId) => `/fees/fee/classroom/${classroomId}`,
      providesTags: ["Fees"],
    }),

    addInvoiceItems: builder.mutation({
      query: ({ invoiceId, items }) => ({
        url: "/fees/invoice",
        method: "PATCH",
        body: { invoice_id: invoiceId, items, action: "add" },
      }),
      invalidatesTags: ["Invoice"],
    }),

    addInvoiceItem: builder.mutation({
      query: ({ invoiceId, ...values }) => ({
        url: `/fees/invoice/${invoiceId}/items`,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Invoice"],
    }),

    editInvoiceItem: builder.mutation({
      query: ({ invoiceId, itemId, ...values }) => ({
        url: `/fees/invoice/${invoiceId}/items/${itemId}`,
        method: "PUT",
        body: { ...values },
      }),
      invalidatesTags: ["Invoice"],
    }),

    deleteInvoiceItem: builder.mutation({
      query: ({ invoiceId, itemId }) => ({
        url: `/fees/invoice/${invoiceId}/items/${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoice"],
    }),

    notifyInvoices: builder.mutation({
      query: (invoiceIds) => ({
        url: "/fees/invoice/notify",
        method: "POST",
        body: { invoice_ids: invoiceIds },
      }),
      invalidatesTags: ["Invoice"],
    }),

    notifyInvoicesBulk: builder.mutation({
      query: ({ classroom_ids, term_id, session_id } = {}) => ({
        url: "/fees/invoice/notify-bulk",
        method: "POST",
        body: {
          ...(classroom_ids?.length ? { classroom_ids } : {}),
          ...(term_id ? { term_id } : {}),
          ...(session_id ? { session_id } : {}),
        },
      }),
      invalidatesTags: ["Invoice"],
    }),
  }),
});

export const {
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useDeleteInvoiceMutation,
  useDeleteInvoicesMutation,
  useConfirmInvoiceMutation,
  useGetSchoolInvoicesQuery,
  useLazyGetClassroomInvoiceReportQuery,
  useLazyPreviewInvoiceQuery,
  useLazyCheckExistingInvoicesQuery,
  useLazyGetClassroomFeesQuery,
  useAddInvoiceItemsMutation,
  useAddInvoiceItemMutation,
  useEditInvoiceItemMutation,
  useDeleteInvoiceItemMutation,
  useNotifyInvoicesMutation,
  useNotifyInvoicesBulkMutation,
} = invoiceApiSlice;
